import { GameMode, HUD_PUBLISH_INTERVAL_TICKS, InputAction } from '../constants';
import type { AudioEngine } from '../audio/AudioEngine';
import type { InputManager } from '../input/InputManager';
import type { RendererPort } from '../render/port';
import type { FramePhaseSampler } from '../performance/FramePhaseSampler';
import type { GameWorld } from '../world/GameWorld';
import type { ComboFeelAdapter } from './ComboFeelAdapter';
import { FixedStepClock } from './FixedStepClock';
import type { RuntimeUIStore } from './RuntimeUIStore';

export class FixedStepLoop {
  readonly clock = new FixedStepClock();
  private raf = 0;
  private running = false;
  private frameCount = 0;
  private fpsWindowStart = 0;
  private fps = 0;
  private visibilityPaused = false;
  private uiTick = 0;

  constructor(
    private readonly world: GameWorld,
    private readonly input: InputManager,
    private readonly renderer: RendererPort,
    private readonly audio: AudioEngine,
    private readonly ui: RuntimeUIStore,
    /** R2 経路でのみ渡る体感層。旧経路では null で、この行は no-op（ルール4）。 */
    private readonly comboFeel: ComboFeelAdapter | null = null,
    private readonly performanceSampler: FramePhaseSampler | null = null,
    private readonly mobilePerformanceMode = false,
  ) {}

  start(): void {
    if (this.running) return;
    this.running = true;
    const now = performance.now();
    this.clock.reset(now);
    this.fpsWindowStart = now;
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.raf = requestAnimationFrame(this.frame);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.raf);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.performanceSampler?.dispose();
  }

  resetClock(): void {
    this.clock.reset(performance.now());
  }

  private readonly onVisibilityChange = (): void => {
    if (document.hidden) {
      this.visibilityPaused = true;
      this.audio.setPaused(true);
    } else {
      this.visibilityPaused = false;
      this.clock.reset(performance.now());
      this.audio.setPaused(this.world.mode === GameMode.Paused);
    }
  };

  private readonly frame = (now: number): void => {
    if (!this.running) return;
    const steps = this.visibilityPaused ? 0 : this.clock.consume(now);
    const sampler = this.performanceSampler;
    sampler?.beginFrame(performance.now());
    const simulationStart = sampler ? performance.now() : 0;
    // Fix 3: collect "does this frame need a publish" instead of calling
    // ui.publish() inside the loop. MAX_CATCH_UP_STEPS (8) exceeds
    // HUD_PUBLISH_INTERVAL_TICKS (6), so a catch-up frame could previously cross
    // the interval boundary twice and fire two React commits in one frame.
    let needsPublish = false;
    let executedSteps = 0;
    for (let i = 0; i < steps; i += 1) {
      executedSteps += 1;
      this.input.beginTick(now);
      this.audio.setPlayerFireHeld(this.input.held[InputAction.Fire] !== 0);
      const modeBefore = this.world.mode;
      if (modeBefore === GameMode.Paused && this.input.pressed[InputAction.Pause] !== 0) {
        this.world.resume();
        this.audio.setPaused(false);
      } else {
        this.world.update(this.input);
        this.comboFeel?.observe(this.world);
        if (this.world.mode === GameMode.Paused) this.audio.setPaused(true);
      }
      this.uiTick += 1;
      const modeChanged = this.world.mode !== modeBefore;
      const liveHudMode =
        this.world.mode === GameMode.Playing
        || this.world.mode === GameMode.CalibrationGate;
      const periodicPublish = this.mobilePerformanceMode
        ? liveHudMode && this.uiTick % HUD_PUBLISH_INTERVAL_TICKS === 0
        : this.uiTick % HUD_PUBLISH_INTERVAL_TICKS === 0;
      if (
        modeChanged
        || periodicPublish
      ) {
        needsPublish = true;
      }
      if (modeChanged) {
        this.clock.reset(now);
        break;
      }
    }
    if (sampler) sampler.recordSimulation(performance.now() - simulationStart, executedSteps);
    const audioStart = sampler ? performance.now() : 0;
    this.audio.update(this.world.musicLayer, this.world.bosses.active, this.world.currentStage);
    if (sampler) sampler.recordAudio(performance.now() - audioStart);
    this.renderer.render(this.world, sampler);
    this.frameCount += 1;
    const elapsed = now - this.fpsWindowStart;
    if (elapsed >= 500) {
      this.fps = Math.round((this.frameCount * 1000) / elapsed);
      this.frameCount = 0;
      this.fpsWindowStart = now;
      needsPublish = true;
    }
    // Exactly one ui.publish() per animation frame, after every step has run.
    if (needsPublish) {
      const publishStart = sampler ? performance.now() : 0;
      sampler?.beginUiPublish(publishStart);
      this.ui.publish(this.world, this.input, this.clock.droppedFrames, this.fps);
      if (sampler) sampler.endUiPublish(performance.now() - publishStart);
    }
    sampler?.endFrame(performance.now());
    this.raf = requestAnimationFrame(this.frame);
  };
}
